/**
 * Uses the Vue-i18n library: http://kazupon.github.io/vue-i18n/introduction.html#sponsors
 *
 * Translations are stored at: https://docs.google.com/spreadsheets/d/1J5RxYvDZOnI86uSkao-EWSHs0YztjmUodJo8RFUo53w/edit#gid=0
 * and converted into locale json in @/locales using 'npm run translations'
 *
 * This really should be changed to load translations at runtime at some point.
 */

import Vue from "vue";
import type { Locale, Path } from "vue-i18n";
import VueI18n from "vue-i18n";
import Key from "@/app/types/common/key";
import type { TranslationKeys } from "@/locales/translationKeys";
import enMessages from "@/locales/en.json";
import { parseLangLocaleOrFallback } from "@/locales/langLocales";
import type { TranslationKeysWithParams } from "@/locales/translationKeysWithParams";
import unexpectedTranslationKeys from "@/locales/unexpectedTranslationKeys";
import assertLogger from "@/app/eventlogging/assertLogger";

const defaultLanguageLocale = "en";
const loadedLanguages = ["en"];

Vue.use(VueI18n);

export const i18n = new VueI18n({
  missing: (locale: Locale, key: Path, vm: Vue | null, values: any) => {
    // We don't always want to create tooltip text so default to empty string if not found.
    if (key.endsWith("_hint")) {
      return "";
    }
    assertLogger.logViolation("Missing Translation : " + key);
    return key.replace(/_/g, " ");
  },
  // Prevents warnings in dev build for missing translations
  silentTranslationWarn: true,

  // Apply 'en' defaults
  locale: defaultLanguageLocale,
  fallbackLocale: defaultLanguageLocale,
  messages: {
    en: enMessages,
  },
});

/**
 * Internal method to set lang locale - use `trySetLanguageLocale` externally.
 */
function setLangLocale(languageLocale: string): string {
  i18n.locale = languageLocale;
  document.querySelector("html")?.setAttribute("lang", languageLocale);
  return languageLocale;
}

/**
 * Tries to set a language-locale, falling back to 'en' if not found.
 * Returns applied language-locale.
 */
export async function trySetLanguageLocale(languageLocale: string) {
  const desiredLangLocale = parseLangLocaleOrFallback(languageLocale);

  // If the desired locale has a resource, and it is not loaded
  if (
    desiredLangLocale.localeResource &&
    !loadedLanguages.includes(desiredLangLocale.langLocale)
  ) {
    // There are rules on how vite can handle dynamic imports - see this before changing:
    // https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
    const langLocaleMessages = await import(
      `@/locales/${desiredLangLocale.localeResource}.json`
    );
    i18n.setLocaleMessage(desiredLangLocale.langLocale, langLocaleMessages);

    // cache that we have already loaded this locale
    loadedLanguages.push(desiredLangLocale.langLocale);
  }

  // otherwise set it and let the i18n library fallback mechanism kick in to
  // use 'en'
  return setLangLocale(desiredLangLocale.langLocale);
}

let cache: Record<string, Key> = {};

// Note: calculating the length using Object.keys(cache).length causes
// noticeable lag, so a separate variable is used to track the number of
// cached keys
let cacheLength = 0;

/**
 * This proxy maps the Keys type to actual Keys. Could be done inside the translationKeys codegen but
 * not sure if that would add more bloat, so copied the 'messages' proxy approach.
 */
export const keys = new Proxy({} as TranslationKeys, {
  get(target: TranslationKeys, prop: keyof TranslationKeys, receiver: any) {
    if (prop.toString().startsWith("__")) {
      // Vue uses a "__ob__" prop to mark an object reactive, we turn all prop
      // requests to strings which confuses vue reactivity in some very specific cases
      // (proxy coming from script setup in this case).
      return undefined;
    }
    if (!cache[prop]) {
      if (cacheLength >= 1000) {
        cache = {};
        cacheLength = 0;
      }

      cache[prop] = Key.fromString(prop);
      cacheLength++;
    }

    return cache[prop];
  },
}) as TranslationKeys;

/**
 * Create a new type from the keys that exposes the keys as prop_key and the translation for the naked prop.
 */
export type Messages = {
  [P in keyof TranslationKeys]: string;
};

export type MessagesWithKeys = Messages & {
  keys: TranslationKeys;
};

/**
 * This proxy adds the backing implementation to the TranslationKeys type.
 */

const messages = new Proxy({} as any, {
  get(target: TranslationKeys, prop: keyof TranslationKeys, receiver: any) {
    const propertyName = prop.toString();
    if (propertyName === "keys") {
      return keys;
    } else if (propertyName.startsWith("__")) {
      // Vue uses a "__ob__" prop to mark an object reactive, we turn all prop
      // requests to strings which confuses vue reactivity in some very specific cases
      // (proxy coming from script setup in this case).
      return undefined;
    } else {
      return tryTranslateFromStringKey(propertyName).toString();
    }
  },
}) as Messages;

const tryTranslateFromStringKey = (
  keyString: string,
  values?: Record<string, any>
) => {
  if (unexpectedTranslationKeys.has(keyString)) {
    assertLogger.logViolation("Unexpected Translation of " + keyString);
  }

  return i18n.t(keyString, values);
};

export const tryTranslate = (key: Key | string, values?: Record<string, any>) =>
  tryTranslateFromStringKey(key instanceof Key ? key.value : key, values);

export const translate = (key: Key, values?: Record<string, any>) => {
  return tryTranslate(key, values).toString();
};

export const translateWithParams = <K extends keyof TranslationKeysWithParams>(
  key: K,
  values: TranslationKeysWithParams[K]
) => translate(Key.fromString(key), values);

const englishOrdinalRules = new Intl.PluralRules("en", { type: "ordinal" });
const suffixes = {
  zero: "",
  many: "",
  one: messages.ordinalPluralOne,
  two: messages.ordinalPluralTwo,
  few: messages.ordinalPluralFew,
  other: messages.ordinalPluralOther,
};

export const appendOrdinalSuffix = (ordinal: number) => {
  const suffix = suffixes[englishOrdinalRules.select(ordinal)];
  return ordinal + suffix;
};

/**
 * Whether the given key is included in the list of available translations - it does not guarentee there is a
 * value for the given key.
 */
export function hasTranslationForKey(key: Key): boolean {
  return i18n.te(key.value);
}

export default messages;

// Composition API
export const useI18n = () => ({
  $m: messages as MessagesWithKeys,
  $t: translate,
  hasTranslationForKey,
  i18n,
});

// Options API
Vue.prototype.$m = messages as MessagesWithKeys;
Vue.prototype.$t = translate;
